/**
 * AccountPage — customer profile (edit name + address) + order history
 * in a single screen.
 *
 * Replaces the need for separate /profile and /order-history routes in
 * the MVP. If the customer base grows we can split these into tabs.
 *
 * Reads:
 *   - profile from AuthContext
 *   - past orders via `list_my_orders()` RPC
 *
 * Writes:
 *   - profile via `update_customer_profile` + `save_customer_default_address` RPCs
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Container, Divider, IconButton, Stack, TextField, Typography,
} from '@mui/material';
import { ChevronRight, Logout, Receipt } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';

interface OrderRow {
  id: string;
  order_number: string | null;
  status: string;
  type: string | null;
  grand_total: number | null;
  currency: string | null;
  item_count: number | null;
  created_at: string;
}

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  pending:    { bg: 'rgba(212,175,55,0.15)', fg: '#D4AF37' },
  confirmed:  { bg: 'rgba(0,200,150,0.15)',  fg: '#00C896' },
  preparing:  { bg: 'rgba(0,200,150,0.15)',  fg: '#00C896' },
  ready:      { bg: 'rgba(0,200,150,0.15)',  fg: '#00C896' },
  completed:  { bg: 'rgba(0,200,150,0.15)',  fg: '#00C896' },
  delivered:  { bg: 'rgba(0,200,150,0.15)',  fg: '#00C896' },
  cancelled:  { bg: 'rgba(239,83,80,0.15)',  fg: '#ef5350' },
};

export const AccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile, refreshProfile, logout, loading: authLoading, user } = useAuth();

  const [name, setName]         = useState('');
  const [street, setStreet]     = useState('');
  const [city, setCity]         = useState('');
  const [postcode, setPostcode] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');

  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  // Bounce back to /auth if not logged in.
  useEffect(() => {
    if (!authLoading && !user) navigate('/auth', { replace: true });
  }, [authLoading, user, navigate]);

  // Pre-fill the form from the AuthContext profile.
  useEffect(() => {
    if (profile) {
      setName(profile.name || '');
      setStreet(profile.street || '');
      setCity(profile.city || '');
      setPostcode(profile.postcode || '');
    }
  }, [profile]);

  // Load order history.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingOrders(true);
      const { data, error } = await supabase.rpc('list_my_orders', { p_limit: 50 });
      if (!alive) return;
      if (error) {
        console.error('[account] list_my_orders failed:', error);
        setOrders([]);
      } else {
        setOrders((data as OrderRow[]) || []);
      }
      setLoadingOrders(false);
    })();
    return () => { alive = false; };
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    setProfileErr('');
    setProfileMsg('');
    try {
      const { error: profErr } = await supabase.rpc('update_customer_profile', {
        p_full_name: name.trim() || null,
      });
      if (profErr) throw profErr;

      if (street.trim()) {
        const { error: addrErr } = await supabase.rpc('save_customer_default_address', {
          p_address_line: street.trim(),
          p_city:         city.trim()     || null,
          p_postcode:     postcode.trim() || null,
          p_country:      profile?.country || 'SA',
          p_label:        'Home',
        });
        if (addrErr) throw addrErr;
      }

      await refreshProfile();
      setProfileMsg('Saved.');
      setTimeout(() => setProfileMsg(''), 2500);
    } catch (err: any) {
      console.error('[account] save failed:', err);
      setProfileErr(err?.message || 'Could not save.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const money = (v: number | null, ccy: string | null): string => {
    const n = Number(v) || 0;
    const c = ccy || 'SAR';
    if (c === 'SAR' || c === 'AED') return `${n.toFixed(2)} ${c}`;
    if (c === 'EUR') return `€${n.toFixed(2)}`;
    return `${n.toFixed(2)} ${c}`;
  };

  const inputSx = {
    '& .MuiInputBase-root': { backgroundColor: 'rgba(255,255,255,0.06)', color: '#fff' },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C896 !important' },
  };

  if (authLoading) {
    return (
      <Container maxWidth="sm" sx={{ pt: 6, pb: 8, textAlign: 'center' }}>
        <CircularProgress sx={{ color: '#00C896' }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 4, pb: 8 }}>
      {/* Profile card */}
      <Box
        sx={{
          backgroundColor: 'rgba(10,37,64,0.6)',
          border: '1px solid rgba(0,200,150,0.2)',
          borderRadius: 3,
          p: { xs: 2.5, sm: 3 },
          color: '#fff',
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 800 }}>My account</Typography>
          <IconButton size="small" onClick={handleLogout} sx={{ color: 'rgba(255,255,255,0.6)' }} title="Log out">
            <Logout fontSize="small" />
          </IconButton>
        </Stack>

        <Stack spacing={1.25} sx={{ mb: 2 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '12px', letterSpacing: '1px' }}>
            SIGNED IN AS
          </Typography>
          <Typography sx={{ color: '#fff', fontSize: '15px', fontWeight: 600 }}>
            {profile?.email || user?.email}
          </Typography>
          {profile?.phone && (
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px' }}>
              {profile.phone}
            </Typography>
          )}
        </Stack>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 2 }} />

        <Stack spacing={2}>
          <TextField fullWidth label="Full name"               value={name}     onChange={(e) => setName(e.target.value)}     sx={inputSx} />
          <TextField fullWidth label="Default delivery address" value={street}   onChange={(e) => setStreet(e.target.value)}   sx={inputSx} multiline minRows={2} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth label="City"     value={city}     onChange={(e) => setCity(e.target.value)}     sx={inputSx} />
            <TextField fullWidth label="Postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} sx={inputSx} />
          </Stack>

          {profileMsg && <Typography sx={{ color: '#00C896', fontSize: '13px' }}>{profileMsg}</Typography>}
          {profileErr && <Typography sx={{ color: '#ef5350', fontSize: '13px' }}>{profileErr}</Typography>}

          <Button
            onClick={saveProfile}
            disabled={savingProfile}
            sx={{
              py: 1.1,
              background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
              color: '#0A2540',
              fontWeight: 700,
              fontSize: '14px',
              '&:hover': { background: 'linear-gradient(135deg, #1AD4A6 0%, #00B387 100%)' },
            }}
          >
            {savingProfile ? <CircularProgress size={18} sx={{ color: '#0A2540' }} /> : 'Save changes'}
          </Button>
        </Stack>
      </Box>

      {/* Orders card */}
      <Box
        sx={{
          backgroundColor: 'rgba(10,37,64,0.6)',
          border: '1px solid rgba(0,200,150,0.2)',
          borderRadius: 3,
          p: { xs: 2, sm: 2.5 },
          color: '#fff',
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <Receipt sx={{ color: '#00C896', fontSize: 22 }} />
          <Typography sx={{ fontSize: '17px', fontWeight: 700 }}>My orders</Typography>
        </Stack>

        {loadingOrders ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} sx={{ color: '#00C896' }} />
          </Stack>
        ) : orders.length === 0 ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', textAlign: 'center', py: 4 }}>
            You haven’t placed any orders yet.
          </Typography>
        ) : (
          <Stack divider={<Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />}>
            {orders.map((o) => {
              const c = STATUS_COLORS[o.status] || { bg: 'rgba(255,255,255,0.08)', fg: 'rgba(255,255,255,0.7)' };
              return (
                <Box
                  key={o.id}
                  onClick={() => navigate(`/order/${o.id}`)}
                  sx={{
                    py: 1.5,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.25 }}>
                      <Typography sx={{ color: '#fff', fontSize: '14px', fontWeight: 700 }}>
                        #{o.order_number || o.id.slice(0, 8)}
                      </Typography>
                      <Chip
                        label={(o.status || 'pending').toUpperCase()}
                        size="small"
                        sx={{
                          height: 20, fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px',
                          background: c.bg, color: c.fg, border: `1px solid ${c.fg}33`,
                        }}
                      />
                    </Stack>
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                      {formatDate(o.created_at)} · {o.item_count || 0} item{o.item_count === 1 ? '' : 's'} · {(o.type || 'order').replace('_', ' ')}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: '#00C896', fontSize: '14px', fontWeight: 700 }}>
                    {money(o.grand_total, o.currency)}
                  </Typography>
                  <ChevronRight sx={{ color: 'rgba(255,255,255,0.4)' }} />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Container>
  );
};
